// What actually happened to the emails and texts a campaign made, from the
// server. The Studio used to call an item "Sent" once its time had passed,
// whether it went, was held for credits, was called off or never ran at all
// (QA 2026-10-02: "Fill Friday's gaps" read "Everything has gone" while its
// text sat held for credits). Posts and Stories are shared by the salon, so
// they stay on the clock. Where each item is up to: itemState.js.
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { marketingApiService } from '../../../services/marketingApiService'
import { LOADING } from './itemState'
import { subscribe, getArea, versionOf } from '../../../../../_utils/syncSummary'

const MESSAGES = ['email', 'sms']
// The newest campaigns are the Studio's; older ones fall back to the clock
const RECENT = 200
// Each check lists up to 200 campaigns with their booking attribution, so it
// isn't cheap: it is only re-run when the app's routine poll says the
// campaigns changed. That poll asks every 20s while something is actually
// going out, once a minute while one is due (the sweep runs every 5 minutes),
// at its usual pace while one is days away, and not at all for campaigns
// once nothing is waiting.
const POLL_MS = 20000
const DUE_POLL_MS = 60000
const MAX_WAIT_MS = 6 * 60 * 60 * 1000
const RETRY_MS = 60000
const ADOPT_MS = 10000
const MAX_RETRIES = 5

// The server ids of the emails and texts these campaigns made
const madeIds = campaigns => (campaigns || []).flatMap(c => Object.values(c.created || {})
  .filter(x => x && x.id && MESSAGES.includes(x.channel)).map(x => String(x.id)))

// When a scheduled campaign is due: scheduledDate is its day (UTC midnight),
// scheduledTime 'HH:mm' on the salon's clock. Close enough to time a check.
const dueAt = (r) => {
  if (!r.scheduledDate) return null
  const day = String(r.scheduledDate).slice(0, 10)
  const t = /^\d{1,2}:\d{2}/.test(r.scheduledTime || '') ? r.scheduledTime : '00:00'
  const when = new Date(`${day}T${t.padStart(5, '0')}:00`).getTime()
  return Number.isFinite(when) ? when : null
}

// How long until it's worth asking again, or null for never
export const nextCheckIn = (rows, now = Date.now()) => {
  if (rows.some(r => r.status === 'sending')) return POLL_MS
  const due = rows.filter(r => r.status === 'scheduled').map(dueAt).filter(x => x !== null)
  if (!due.length) return null
  const wait = Math.min(...due) - now
  return wait <= 0 ? DUE_POLL_MS : Math.min(Math.max(wait, DUE_POLL_MS), MAX_WAIT_MS)
}

// { [serverId]: { status, heldReason, archivedFrom, deliveredCount, totalRecipients } },
// or LOADING until it has loaded.
export const useServerStatuses = (campaigns) => {
  const dispatch = useDispatch()
  const [map, setMap] = useState(LOADING)
  const ids = madeIds(campaigns)
  const key = ids.sort().join(',')
  useEffect(() => {
    if (!ids.length) { setMap({}); return undefined }
    // A new set of campaigns: don't show the last set's answers (or the
    // clock's guess) while this one loads
    setMap(LOADING)
    let alive = true
    let timer = null
    let failures = 0
    let unfollow = null
    let loadedVersion = null
    let loadedAt = 0
    let wait = null
    let loading = false
    // Follow the app's routine poll (/sync/summary) while anything is going
    // out or due: refetch the list only when the campaigns' version moves.
    // While one is sending the poll runs every 20s; while one is due, every
    // minute; otherwise at its usual pace. Nothing to wait for: stop.
    const follow = () => {
      if (wait === null) { if (unfollow) { unfollow(); unfollow = null } return }
      const cadence = wait <= DUE_POLL_MS ? { base: wait, max: DUE_POLL_MS } : null
      if (unfollow) { unfollow.setCadence(cadence); return }
      unfollow = subscribe({
        areas: ['campaigns'],
        cadence,
        onUpdate: (data, event) => {
          const v = data.campaigns
          if (event.error || !v || loading) return
          // Older server (no /sync/summary): the old timing, by the clock.
          if (v.legacy) { if (Date.now() - loadedAt >= wait) load(); return }
          // The first version seen, seconds after a load that ran before the
          // poll knew about campaigns: adopt it rather than list them all
          // again (the list is not cheap). Anything later that moves it
          // reloads as usual.
          if (loadedVersion === null && Date.now() - loadedAt < ADOPT_MS) { loadedVersion = v.version; return }
          if (v.version !== loadedVersion) load()
        }
      })
    }
    const load = async () => {
      loading = true
      loadedVersion = versionOf(getArea('campaigns'))
      try {
        const rows = await marketingApiService.getCampaigns(dispatch, { currentPage: 1, limit: RECENT })
        loading = false
        if (!alive) return
        failures = 0
        loadedAt = Date.now()
        const mine = (rows || []).filter(r => ids.includes(String(r.id)))
        const next = {}
        mine.forEach((r) => {
          const raw = r._raw || {}
          next[String(r.id)] = {
            status: r.status,
            heldReason: r.heldReason || null,
            archivedFrom: raw.archivedFrom || null,
            deliveredCount: raw.deliveredCount || 0,
            totalRecipients: raw.totalRecipients || 0
          }
        })
        setMap(next)
        wait = nextCheckIn(mine)
        follow()
      } catch (e) {
        loading = false
        loadedVersion = null
        // Offline or refused: leave it on the clock rather than say nothing,
        // and try again a few times rather than stop checking for good
        if (!alive) return
        setMap(m => (m === LOADING ? {} : m))
        if (++failures <= MAX_RETRIES) timer = setTimeout(load, RETRY_MS)
      }
    }
    load()
    return () => { alive = false; clearTimeout(timer); if (unfollow) unfollow() }
  }, [key, dispatch]) // eslint-disable-line react-hooks/exhaustive-deps
  return map
}
